(function () {
  // Intl formatters cost far more to construct than to use, so build each one
  // once up front. These used to be constructed inside the helpers below, which
  // meant rebuilding them once per badge, per render.
  const hongKongDateKeyFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const hongKongWeekdayFormat = new Intl.DateTimeFormat('en-AU', {
    weekday: 'long',
    timeZone: 'Asia/Hong_Kong',
  });

  const EMPTY_CONFIG = {blackoutDateSet: new Set(), brandDeliveryAfterMap: null};

  // Delivery-after values are static strings repeated across many cards, so the
  // resulting date key is worth memoising by raw value.
  const deliveryAfterKeyCache = new Map();

  let configCache = null;
  let configSource = null;
  let cutoffTimer = null;

  function parseMaybeJson(value, fallback) {
    if (value == null || value === '') return fallback;
    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  function parseList(value, fallback) {
    const parsed = parseMaybeJson(value, fallback);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (typeof parsed === 'string') {
      const values = parsed.split(',').map((item) => item.trim()).filter(Boolean);
      return values.length ? values : fallback;
    }
    return fallback;
  }

  function normalizeBrand(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function getHongKongDate(startDate) {
    return new Date(startDate.toLocaleString('en-US', {timeZone: 'Asia/Hong_Kong'}));
  }

  function getHongKongDateKey(date) {
    const parts = hongKongDateKeyFormat.formatToParts(date);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    return `${year}-${month}-${day}`;
  }

  function isNonDispatchDay(date, blackoutDateSet) {
    const day = date.getDay();
    return day === 0 || day === 6 || blackoutDateSet.has(getHongKongDateKey(date));
  }

  function getBadgeText(blackoutDateSet, now) {
    const hongKongNow = getHongKongDate(now);
    const isAfterCutoff =
      hongKongNow.getHours() > 13 ||
      (hongKongNow.getHours() === 13 && hongKongNow.getMinutes() >= 1);

    if (!isNonDispatchDay(hongKongNow, blackoutDateSet) && !isAfterCutoff) {
      return 'Ships today';
    }

    // Find the next day we actually dispatch (skips weekends and public holidays).
    const nextDispatch = new Date(hongKongNow);
    do {
      nextDispatch.setDate(nextDispatch.getDate() + 1);
    } while (isNonDispatchDay(nextDispatch, blackoutDateSet));

    const tomorrow = new Date(hongKongNow);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (getHongKongDateKey(nextDispatch) === getHongKongDateKey(tomorrow)) {
      return 'Ships tomorrow';
    }

    return `Ships ${hongKongWeekdayFormat.format(nextDispatch)}`;
  }

  function readShopConfig() {
    const node = document.getElementById('ships-badge-shop-config');
    if (!node) return EMPTY_CONFIG;

    // The config is static markup, so only re-parse when it actually changes.
    const source = node.textContent;
    if (configCache && source === configSource) return configCache;

    configSource = source;

    let config;
    try {
      config = JSON.parse(source);
    } catch {
      configCache = EMPTY_CONFIG;
      return configCache;
    }

    const brandDeliveryAfter = parseMaybeJson(config.brandDeliveryAfter, null);
    let brandDeliveryAfterMap = null;

    if (brandDeliveryAfter && typeof brandDeliveryAfter === 'object' && !Array.isArray(brandDeliveryAfter)) {
      // Precompute the normalised lookup so each badge costs a single Map hit
      // instead of a scan across every brand. First key wins, as before.
      brandDeliveryAfterMap = new Map();
      Object.keys(brandDeliveryAfter).forEach((brand) => {
        const key = normalizeBrand(brand);
        if (!brandDeliveryAfterMap.has(key)) brandDeliveryAfterMap.set(key, brandDeliveryAfter[brand]);
      });
    }

    configCache = {
      blackoutDateSet: new Set(parseList(config.blackoutDates, [])),
      brandDeliveryAfterMap,
    };

    return configCache;
  }

  function getDeliveryAfterKey(badge, brandDeliveryAfterMap) {
    let value = badge.dataset.deliveryAfter;

    if (!value && brandDeliveryAfterMap) {
      value = brandDeliveryAfterMap.get(normalizeBrand(badge.dataset.vendor));
    }

    if (!value) return null;

    const raw = String(value).trim();
    if (deliveryAfterKeyCache.has(raw)) return deliveryAfterKeyCache.get(raw);

    const date = new Date(raw);
    const key = Number.isNaN(date.getTime()) ? null : getHongKongDateKey(date);
    deliveryAfterKeyCache.set(raw, key);

    return key;
  }

  function renderAll() {
    const badges = document.querySelectorAll('[data-ships-badge-card]');
    if (!badges.length) return;

    const {blackoutDateSet, brandDeliveryAfterMap} = readShopConfig();
    const now = new Date();
    const text = getBadgeText(blackoutDateSet, now);
    const todayKey = getHongKongDateKey(now);

    badges.forEach((badge) => {
      // A future delivery-after date means the item can't dispatch yet.
      const deliveryAfterKey = getDeliveryAfterKey(badge, brandDeliveryAfterMap);
      if (deliveryAfterKey && deliveryAfterKey > todayKey) {
        if (!badge.hidden) badge.hidden = true;
        return;
      }

      // Only write when the value actually changes. Assigning textContent
      // replaces the child text node even when the string is identical, and the
      // observer below would see that as a DOM change and re-render in
      // response, leaving the page in a permanent render loop.
      if (badge.textContent !== text) badge.textContent = text;
      if (badge.hidden) badge.hidden = false;
    });
  }

  function hasBadgeElement(record) {
    for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.matches('[data-ships-badge-card]') || node.querySelector('[data-ships-badge-card]')) {
        return true;
      }
    }

    return false;
  }

  // The badge text only changes at two moments in the day: the 1pm Hong Kong
  // dispatch cutoff, and midnight. Wake up for those rather than polling.
  function msUntilNextChange(now) {
    const hongKongNow = getHongKongDate(now);
    const next = new Date(hongKongNow);

    if (hongKongNow.getHours() < 13 || (hongKongNow.getHours() === 13 && hongKongNow.getMinutes() < 1)) {
      next.setHours(13, 1, 0, 0);
    } else {
      next.setHours(24, 0, 0, 0);
    }

    // Both dates share a frame of reference, so the difference is a true duration.
    return Math.max(1000, next.getTime() - hongKongNow.getTime());
  }

  function scheduleNextChange() {
    if (cutoffTimer) clearTimeout(cutoffTimer);

    cutoffTimer = setTimeout(() => {
      cutoffTimer = null;
      renderAll();
      scheduleNextChange();
    }, msUntilNextChange(new Date()));
  }

  function initialize() {
    renderAll();

    // Collection filtering/pagination swaps cards in without a page load. Only
    // added elements matter here, so our own text writes never wake this up.
    let scheduled = null;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      if (!records.some(hasBadgeElement)) return;

      scheduled = setTimeout(() => {
        scheduled = null;
        renderAll();
      }, 100);
    });
    observer.observe(document.body, {childList: true, subtree: true});

    scheduleNextChange();

    // A backgrounded tab can have its timers throttled, and the device may sleep
    // straight through the cutoff, so re-check on the way back.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      renderAll();
      scheduleNextChange();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {once: true});
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', renderAll);
})();
